import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { PILLARS, METHOD_STEPS, IMAGES } from "../data/content";
import { Motif } from "../components/Motifs";
import { Reveal } from "../components/Primitives";

export default function Method() {
  const [active, setActive] = useState(0);
  const p = PILLARS[active];

  return (
    <div data-testid="method-page">
      {/* HERO */}
      <section className="pt-36 pb-16 md:pt-44 md:pb-20 bg-[#120726] grain text-center">
        <div className="container-loft max-w-3xl mx-auto">
          <Reveal>
            <p className="overline mb-5">The Framework Behind Everything</p>
            <h1 className="font-serif text-5xl md:text-7xl font-light leading-[1.03] text-[#EFE7FA]">
              The <span className="text-gradient">5L Method™</span>
            </h1>
            <p className="mt-7 text-lg md:text-xl font-light leading-relaxed text-[#EFE7FA]/75">
              Five principles — London, Loft, Lyfe, Lotus, and Lighthouse — that move you through
              healing, clarity, and elevation toward a life and legacy you love.
            </p>
          </Reveal>
        </div>
      </section>

      {/* INTERACTIVE PILLARS */}
      <section className="pb-24 md:pb-32 bg-[#1a0d33]">
        <div className="container-loft">
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {PILLARS.map((pill, i) => (
              <button
                key={pill.key}
                onClick={() => setActive(i)}
                data-testid={`method-tab-${pill.key}`}
                className={`px-6 py-3 rounded-full text-sm tracking-wide transition-all duration-300 border ${
                  active === i
                    ? "bg-[#5B2C83] text-white border-[#5B2C83] shadow-[0_10px_30px_rgba(91,44,131,0.25)]"
                    : "bg-white/5 text-[#EFE7FA]/70 border-[#C0C0C0]/40 hover:border-[#5B2C83]"
                }`}
              >
                {pill.name}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={p.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="grid md:grid-cols-12 gap-10 items-center max-w-5xl mx-auto"
            >
              <div className="md:col-span-5 flex justify-center">
                <div className="relative grain">
                  <div className="glow-ice absolute inset-0 scale-150" />
                  <div className="relative z-10 h-52 w-52 rounded-full bg-white/70 border border-[#C0C0C0]/40 grid place-items-center shadow-[0_20px_60px_rgba(168,216,240,0.5)]">
                    <Motif name={p.motif} className="w-24 h-24" stroke={p.color} strokeWidth={1} />
                  </div>
                </div>
              </div>
              <div className="md:col-span-7">
                <p className="overline mb-3" style={{ color: p.color }}>Pillar 0{active + 1}</p>
                <h2 className="font-serif text-5xl font-light" style={{ color: p.color }}>{p.name}</h2>
                <p className="mt-3 text-lg uppercase tracking-widest text-[#EFE7FA]/50">{p.title}</p>
                <p className="mt-5 text-lg font-light leading-relaxed text-[#EFE7FA]/75">{p.text}</p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* THE METHOD IN MOTION */}
      <section className="py-24 md:py-32 bg-[#120726]">
        <div className="container-loft">
          <Reveal>
            <p className="overline mb-4 text-center">The Journey</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-center text-[#EFE7FA]">
              Bloom · Illuminate · Elevate · Legacy · Live
            </h2>
          </Reveal>
          <div className="mt-16 max-w-4xl mx-auto">
            {METHOD_STEPS.map((s, i) => (
              <Reveal key={s.word} delay={i * 0.08}>
                <div className="flex gap-6 md:gap-10 items-start py-8 border-b border-[#C0C0C0]/30 group">
                  <span className="font-serif text-5xl md:text-6xl font-light text-[#A8D8F0] group-hover:text-[#5B2C83] transition-colors duration-500 w-16 shrink-0">
                    0{i + 1}
                  </span>
                  <div>
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <h3 className="font-serif text-3xl font-light text-[#EFE7FA]">{s.word}</h3>
                      <span className="overline text-[#0B7A5A]">{s.pillar}</span>
                    </div>
                    <p className="mt-3 text-lg font-light leading-relaxed text-[#EFE7FA]/70">{s.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="text-center mt-16">
              <Link to="/services" className="btn-primary" data-testid="method-cta">
                Begin The Method <ArrowRight size={18} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
